import {
  TextCapitalized,
  Spacer,
  SidePanelDS,
  ButtonDS,
  EditOutlined,
  triggerToastDS,
} from "@qivia/ui";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PublicTransactionsPropsType } from "../transactionsAPI";
import styled from "styled-components";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { UpdateTransactionDetailsInput } from "./updateTransactionDetailsInput";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import {
  transactionsSlice,
  selectUpdateTransactionDetailsStatus,
} from "../transactionsSlice";

const transactionInformations = [
  "merchantCity",
  "fuel",
  "mileage",
  "quantity",
  "consumption",
  "carbonEmission",
];

const editableInformation = ["mileage"];

export const TransactionsDetails = (props: {
  transaction: PublicTransactionsPropsType;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const updateTransactionDetailsStatus = useAppSelector(
    selectUpdateTransactionDetailsStatus,
  );

  const hasUnits = (label: string) => {
    return ["mileage", "quantity", "consumption", "carbonEmission"].includes(
      label,
    );
  };

  const findKeysToTranslate = useCallback(
    (key: keyof PublicTransactionsPropsType, value: string) => {
      switch (key) {
        case "fuel":
          return t(`transactions.fuel.${value}`);
        default:
          return value;
      }
    },
    [t],
  );

  const [editingInformation, setEditingInformation] = useState<string | null>(
    null,
  );

  const EditButton = (props: { informationLabel: string }) => {
    return (
      <ButtonDS
        height="2.5"
        singleIcon={{ icon: <EditOutlined />, size: "S" }}
        onClick={() => setEditingInformation(props.informationLabel)}
        format="hug"
        buttonType="primary"
      />
    );
  };

  useEffect(() => {
    if (updateTransactionDetailsStatus === "success") {
      triggerToastDS(
        t(`transactions.updateDetails.${editingInformation}.success`) || "",
        "SUCCESS",
      );
      dispatch(transactionsSlice.actions.resetUpdateTransactionDetailsStatus());
      dispatch(transactionsSlice.actions.resetTransactionsListStatus());
      setEditingInformation(null);
    } else if (updateTransactionDetailsStatus === "failed") {
      triggerToastDS(
        t(`transactions.updateDetails.${editingInformation}.failure`) || "",
        "ERROR",
      );
      dispatch(transactionsSlice.actions.resetUpdateTransactionDetailsStatus());
      setEditingInformation(null);
    }
  }, [updateTransactionDetailsStatus, t, dispatch, editingInformation]);

  return (
    <>
      <TextCapitalized>
        <StyledLabel>{t(`transactions.informations.label.title`)}</StyledLabel>
      </TextCapitalized>
      {transactionInformations.map((informationLabel) => {
        const informationDisplay =
          props.transaction[
            informationLabel as keyof PublicTransactionsPropsType
          ];

        const informationDisplayTranslated = findKeysToTranslate(
          informationLabel as keyof PublicTransactionsPropsType,
          informationDisplay as string,
        );

        const isEditable =
          props.transaction.status !== "REJECTED" &&
          props.transaction.type === "EXPENSE" &&
          editableInformation.includes(informationLabel);

        return (
          <div key={informationLabel}>
            <Spacer y={2} />
            <SidePanelDS.SectionRow
              label={
                <StyledInformations key={informationLabel}>
                  <TextCapitalized>
                    {t(`transactions.informations.label.${informationLabel}`)}
                  </TextCapitalized>
                </StyledInformations>
              }
              value={
                isEditable && editingInformation === informationLabel ? (
                  <UpdateTransactionDetailsInput
                    informationLabel={informationLabel}
                    transactionUuid={props.transaction.uuid}
                    value={informationDisplayTranslated}
                  />
                ) : (
                  <TextCapitalized>
                    <StyledInformations key={informationLabel}>
                      {!informationDisplay ||
                      informationDisplay.toString() === "0" ? (
                        "-"
                      ) : (
                        <StyledRow>
                          <TextCapitalized>
                            {informationDisplayTranslated}
                          </TextCapitalized>
                          <Spacer x={0.25} />
                          {hasUnits(informationLabel) && (
                            <StyledUnits>
                              <TextCapitalized>
                                {t(
                                  `transactions.informations.units.${informationLabel}`,
                                )}
                              </TextCapitalized>
                            </StyledUnits>
                          )}
                        </StyledRow>
                      )}
                      {isEditable ? (
                        <StyledEditable>
                          <Spacer x={0.5} />
                          <EditButton informationLabel={informationLabel} />
                        </StyledEditable>
                      ) : (
                        ""
                      )}
                    </StyledInformations>
                  </TextCapitalized>
                )
              }
            />
          </div>
        );
      })}
    </>
  );
};

const StyledRow = styled.div`
  display: flex;
  align-items: flex-end;
`;

const StyledLabel = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/darkGrey"]};
`;
const StyledUnits = styled.div`
  ${typographies["Body/XXS"]}
  color: ${colors["colors/text/darkGrey"]};
`;

const StyledInformations = styled.div`
  display: flex;
  align-items: center;
  ${typographies["Body/L"]};
  color: ${colors["colors/text/black"]};
`;

const StyledEditable = styled.div`
  display: flex;
  align-items: center;
`;
